import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';
import { DadosContrato } from '@/utils/contratoUtils';

// Funções de formatação
const formatarCPF = (cpf: string) => {
  const cpfLimpo = cpf.replace(/\D/g, '');
  return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatarCEP = (cep: string) => {
  const cepLimpo = cep.replace(/\D/g, '');
  return cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2');
};

const formatarCNPJ = (cnpj: string) => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatarValor = (valor: number | string) => {
  const valorNumerico = typeof valor === 'string' ? parseFloat(valor) || 0 : valor;
  return valorNumerico.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatarValorInput = (valor: string) => {
  // Remove tudo que não é número ou ponto decimal
  const valorLimpo = valor.replace(/[^\d.,]/g, '');
  
  // Converte vírgula para ponto
  const valorComPonto = valorLimpo.replace(',', '.');
  
  // Remove pontos que não sejam decimais (separadores de milhar)
  const partes = valorComPonto.split('.');
  if (partes.length > 2) {
    // Se há mais de um ponto, mantém apenas o último como decimal
    const parteInteira = partes.slice(0, -1).join('');
    const parteDecimal = partes[partes.length - 1];
    return `${parteInteira}.${parteDecimal}`;
  }
  
  return valorComPonto;
};


interface ContratoFormularioProps {
  modo: 'formulario' | 'edicao';
  propostaSelecionada?: PropostaParaContrato | null;
  contratoEditando?: ContratoWithDetails | null;
  dadosContrato: DadosContrato;
  setDadosContrato: React.Dispatch<React.SetStateAction<DadosContrato>>;
  loadingAction: boolean;
  onSalvar: () => void;
  onCancelar: () => void;
  validarCamposObrigatorios: () => boolean;
  obterMensagemValidacao: () => string | null;
  calcularValorParcela: (valorTotal: number, numeroParcelas: number) => number;
  atualizarValoresKit: (formaPagamento: string, valorKit: number) => void;
  atualizarValoresIntegracao: (modalidade: number, valorTotal: number) => void;
  temMudancas: () => boolean;
}

export default function ContratoFormulario({
  modo,
  propostaSelecionada,
  contratoEditando,
  dadosContrato,
  setDadosContrato,
  loadingAction,
  onSalvar,
  onCancelar,
  validarCamposObrigatorios,
  obterMensagemValidacao,
  calcularValorParcela,
  atualizarValoresKit,
  atualizarValoresIntegracao,
  temMudancas
}: ContratoFormularioProps) {
  const [campoEditando, setCampoEditando] = useState<string | null>(null);
  const [valoresEditando, setValoresEditando] = useState<{
    kit_valor_pagamento: string;
    integracao_valor_total: string;
    kit_valor_parcela: string;
    integracao_valor_parcela: string;
    integracao_valor_parcela_assinatura: string;
    integracao_valor_parcela_inicio_obra: string;
    integracao_valor_parcela_conclusao_obra: string;
    integracao_valor_parcela_ligacao_definitiva: string;
  }>({
    kit_valor_pagamento: '',
    integracao_valor_total: '',
    kit_valor_parcela: '',
    integracao_valor_parcela: '',
    integracao_valor_parcela_assinatura: '',
    integracao_valor_parcela_inicio_obra: '',
    integracao_valor_parcela_conclusao_obra: '',
    integracao_valor_parcela_ligacao_definitiva: ''
  });
  
  // Normaliza para 'PF' | 'PJ' considerando possíveis valores 'fisica'/'juridica'
  const tipoCliente = ((): 'PF' | 'PJ' | undefined => {
    if (modo === 'formulario') {
      const t = propostaSelecionada?.lead_tipo;
      return t === 'PF' || t === 'PJ' ? t : undefined;
    }
    const t = contratoEditando?.lead?.tipo as string | undefined;
    if (t === 'PF' || t === 'PJ') return t;
    if (t === 'fisica') return 'PF';
    if (t === 'juridica') return 'PJ';
    // Fallback por preenchimento de campos
    if (contratoEditando?.nome_cliente) return 'PF';
    return 'PJ';
  })();


  return (
    <>
      {/* Informações da Empresa */}
      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-base sm:text-lg font-semibold">Informações da Empresa</h3>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Preencha as informações da empresa contratada
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <Label htmlFor="razao_social_empresa">Razão Social *</Label>
            <Input
              id="razao_social_empresa"
              value={dadosContrato.razao_social_empresa}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, razao_social_empresa: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="nome_fantasia_empresa">Nome Fantasia *</Label>
            <Input
              id="nome_fantasia_empresa"
              value={dadosContrato.nome_fantasia_empresa}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, nome_fantasia_empresa: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="cnpj_empresa">CNPJ *</Label>
            <Input
              id="cnpj_empresa"
              value={formatarCNPJ(dadosContrato.cnpj_empresa)}
              onChange={(e) => {
                const cnpjLimpo = e.target.value.replace(/\D/g, '');
                setDadosContrato(prev => ({ ...prev, cnpj_empresa: cnpjLimpo }));
              }}
              maxLength={18}
              placeholder="00.000.000/0000-00"
            />
          </div>
        </div>

        {/* Endereço da Empresa */}
        <div className="mt-3 sm:mt-4">
          <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">Endereço da Empresa</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="endereco_empresa">Endereço *</Label>
              <Input
                id="endereco_empresa"
                value={dadosContrato.endereco_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, endereco_empresa: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="numero_empresa">Número *</Label>
              <Input
                id="numero_empresa"
                value={dadosContrato.numero_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, numero_empresa: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="bairro_empresa">Bairro *</Label>
              <Input
                id="bairro_empresa"
                value={dadosContrato.bairro_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, bairro_empresa: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="cidade_empresa">Cidade *</Label>
              <Input
                id="cidade_empresa"
                value={dadosContrato.cidade_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, cidade_empresa: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="estado_empresa">Estado *</Label>
              <Input
                id="estado_empresa"
                value={dadosContrato.estado_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, estado_empresa: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="cep_empresa">CEP *</Label>
              <Input
                id="cep_empresa"
                value={formatarCEP(dadosContrato.cep_empresa)}
                onChange={(e) => {
                  const cepLimpo = e.target.value.replace(/\D/g, '');
                  setDadosContrato(prev => ({ ...prev, cep_empresa: cepLimpo }));
                }}
                maxLength={9}
                placeholder="00000-000"
              />
            </div>
          </div>
        </div>

        {/* Representante Legal da Empresa */}
        <div className="mt-3 sm:mt-4">
          <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">Representante Legal</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="representante_legal_empresa">Nome Completo *</Label>
              <Input
                id="representante_legal_empresa"
                value={dadosContrato.representante_legal_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, representante_legal_empresa: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <Label htmlFor="cpf_representante_empresa">CPF *</Label>
              <Input
                id="cpf_representante_empresa"
                value={formatarCPF(dadosContrato.cpf_representante_empresa)}
                onChange={(e) => {
                  const cpfLimpo = e.target.value.replace(/\D/g, '');
                  setDadosContrato(prev => ({ ...prev, cpf_representante_empresa: cpfLimpo }));
                }}
                maxLength={14}
                placeholder="000.000.000-00"
              />
            </div>
            <div>
              <Label htmlFor="cargo_representante_empresa">Cargo *</Label>
              <Input
                id="cargo_representante_empresa"
                value={dadosContrato.cargo_representante_empresa}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, cargo_representante_empresa: e.target.value }))}
                placeholder="Ex: Diretor, Gerente, etc."
              />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Informações do Cliente */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Informações do Cliente</h3>
        
        {tipoCliente === 'PF' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="nome_cliente">Nome Completo *</Label>
              <Input
                id="nome_cliente"
                value={dadosContrato.nome_cliente}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, nome_cliente: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="cpf">CPF *</Label>
              <Input
                id="cpf"
                value={formatarCPF(dadosContrato.cpf)}
                onChange={(e) => {
                  const cpfLimpo = e.target.value.replace(/\D/g, '');
                  setDadosContrato(prev => ({ ...prev, cpf: cpfLimpo }));
                }}
                maxLength={14}
                placeholder="000.000.000-00"
              />
            </div>
            <div>
              <Label htmlFor="estado_civil">Estado Civil *</Label>
              <Input
                id="estado_civil"
                value={dadosContrato.estado_civil}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, estado_civil: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="profissao">Profissão *</Label>
              <Input
                id="profissao"
                value={dadosContrato.profissao}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, profissao: e.target.value }))}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="razao_social">Razão Social *</Label>
              <Input
                id="razao_social"
                value={dadosContrato.razao_social}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, razao_social: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="cnpj">CNPJ *</Label>
              <Input
                id="cnpj"
                value={formatarCNPJ(dadosContrato.cnpj)}
                onChange={(e) => {
                  const cnpjLimpo = e.target.value.replace(/\D/g, '');
                  setDadosContrato(prev => ({ ...prev, cnpj: cnpjLimpo }));
                }}
                maxLength={18}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div>
              <Label htmlFor="representante_legal">Representante Legal *</Label>
              <Input
                id="representante_legal"
                value={dadosContrato.representante_legal}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, representante_legal: e.target.value.toUpperCase() }))}
              />
            </div>
            <div>
              <Label htmlFor="cpf_representante">CPF do Representante *</Label>
              <Input
                id="cpf_representante"
                value={formatarCPF(dadosContrato.cpf_representante)}
                onChange={(e) => {
                  const cpfLimpo = e.target.value.replace(/\D/g, '');
                  setDadosContrato(prev => ({ ...prev, cpf_representante: cpfLimpo }));
                }}
                maxLength={14}
                placeholder="000.000.000-00"
              />
            </div>
          </div>
        )}
      </div>

      <Separator />

      {/* Endereço */}
      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-base sm:text-lg font-semibold">Endereço</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="endereco">Endereço *</Label>
            <Input
              id="endereco"
              value={dadosContrato.endereco}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, endereco: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="numero">Número *</Label>
            <Input
              id="numero"
              value={dadosContrato.numero}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, numero: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="bairro">Bairro *</Label>
            <Input
              id="bairro"
              value={dadosContrato.bairro}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, bairro: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="cidade">Cidade *</Label>
            <Input
              id="cidade"
              value={dadosContrato.cidade}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, cidade: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="estado">Estado *</Label>
            <Input
              id="estado"
              value={dadosContrato.estado}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, estado: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="cep">CEP *</Label>
            <Input
              id="cep"
              value={formatarCEP(dadosContrato.cep)}
              onChange={(e) => {
                const cepLimpo = e.target.value.replace(/\D/g, '');
                setDadosContrato(prev => ({ ...prev, cep: cepLimpo }));
              }}
              maxLength={9}
              placeholder="00000-000"
            />
          </div>
        </div>
      </div>

      <Separator />

      {/* Seção de Pagamentos Separados */}
      <div className="space-y-6 sm:space-y-8">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Formas de Pagamento</h3>
        
        {/* Pagamento do Kit */}
        <div className="space-y-3 sm:space-y-4 p-4 sm:p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-base sm:text-lg font-semibold text-blue-800">💰 Pagamento do Kit</h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="kit_forma_pagamento">Forma de Pagamento do Kit *</Label>
              <Select 
                value={dadosContrato.kit_forma_pagamento} 
                onValueChange={(value) => {
                  setDadosContrato(prev => {
                    atualizarValoresKit(value, prev.kit_valor_pagamento);
                    return { ...prev, kit_forma_pagamento: value };
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a forma de pagamento do kit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                  <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                  <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {dadosContrato.kit_forma_pagamento === 'cartao_credito' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="kit_numero_parcelas">Número de Parcelas do Kit</Label>
                  <Select 
                    value={dadosContrato.kit_numero_parcelas.toString()} 
                    onValueChange={(value) => {
                      const parcelas = parseInt(value);
                      setDadosContrato(prev => ({ 
                        ...prev, 
                        kit_numero_parcelas: parcelas
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o número de parcelas" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} parcela{num > 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="kit_valor_parcela">Valor de cada Parcela (R$)</Label>
                  <Input
                    id="kit_valor_parcela"
                    value={campoEditando === 'kit_valor_parcela' 
                      ? valoresEditando.kit_valor_parcela || ''
                      : formatarValor(dadosContrato.kit_valor_parcela)}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setValoresEditando(prev => ({ ...prev, kit_valor_parcela: valor }));
                    }}
                    onFocus={() => {
                      setCampoEditando('kit_valor_parcela');
                      setValoresEditando(prev => ({ 
                        ...prev, 
                        kit_valor_parcela: dadosContrato.kit_valor_parcela.toString() 
                      }));
                    }}
                    onBlur={() => {
                      setCampoEditando(null);
                      const valorLimpo = valoresEditando.kit_valor_parcela?.replace(',', '.') || '0';
                      const valor = parseFloat(valorLimpo) || 0;
                      setDadosContrato(prev => ({ 
                        ...prev, 
                        kit_valor_parcela: valor 
                      }));
                    }}
                    placeholder="0,00"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="kit_valor_pagamento">Valor do Kit para Pagamento (R$)</Label>
              <Input
                id="kit_valor_pagamento"
                value={campoEditando === 'kit_valor_pagamento' 
                  ? valoresEditando.kit_valor_pagamento
                  : formatarValor(dadosContrato.kit_valor_pagamento)}
                onChange={(e) => {
                  // Permite entrada de vírgula e ponto
                  const valor = e.target.value;
                  setValoresEditando(prev => ({ ...prev, kit_valor_pagamento: valor }));
                }}
                onFocus={() => {
                  setCampoEditando('kit_valor_pagamento');
                  setValoresEditando(prev => ({ 
                    ...prev, 
                    kit_valor_pagamento: dadosContrato.kit_valor_pagamento.toString() 
                  }));
                }}
                onBlur={() => {
                  setCampoEditando(null);
                  const valorLimpo = (valoresEditando.kit_valor_pagamento ?? '').replace(',', '.');
                  const valor = parseFloat(valorLimpo) || 0;
                  // Atualiza valor do kit e valor total do contrato com o valor digitado (pode divergir da proposta)
                  atualizarValoresKit(dadosContrato.kit_forma_pagamento, valor);
                }}
                placeholder="0,00"
              />
            </div>
            <div>
              <Label htmlFor="kit_observacoes">Observações do Kit</Label>
              <Input
                id="kit_observacoes"
                value={dadosContrato.kit_observacoes}
                onChange={(e) => setDadosContrato(prev => ({ ...prev, kit_observacoes: e.target.value }))}
                placeholder="Observações sobre o pagamento do kit (opcional)"
              />
            </div>
          </div>

          {/* Resumo do Pagamento do Kit */}
          {dadosContrato.kit_forma_pagamento === 'cartao_credito' && dadosContrato.kit_numero_parcelas > 1 && (
            <div className="bg-blue-100 p-4 rounded-lg">
              <h5 className="font-semibold text-blue-800 mb-2">Resumo do Pagamento do Kit</h5>
              <p className="text-sm text-blue-700">
                <strong>Valor Original:</strong> R$ {dadosContrato.kit_valor_pagamento.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br/>
                <strong>Número de Parcelas:</strong> {dadosContrato.kit_numero_parcelas}x<br/>
                <strong>Valor de cada Parcela:</strong> R$ {dadosContrato.kit_valor_parcela.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}<br/>
                <strong>Valor Total com Juros:</strong> R$ {(dadosContrato.kit_valor_parcela * dadosContrato.kit_numero_parcelas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
        </div>

        {/* Pagamento da Integração */}
        <div className="space-y-3 sm:space-y-4 p-4 sm:p-6 bg-green-50 rounded-lg border border-green-200">
          <h4 className="text-base sm:text-lg font-semibold text-green-800">🔧 Pagamento da Integração</h4>
          
          {/* Modalidade de Pagamento da Integração */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="integracao_modalidade_pagamento">Modalidade de Pagamento</Label>
              <Select 
                value={dadosContrato.integracao_modalidade_pagamento.toString()} 
                onValueChange={(value) => {
                  const modalidade = parseInt(value);
                  setDadosContrato(prev => {
                    atualizarValoresIntegracao(modalidade, prev.integracao_valor_total);
                    return { ...prev, integracao_modalidade_pagamento: modalidade };
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a modalidade de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Pagamento integral na assinatura</SelectItem>
                  <SelectItem value="2">2 - Assinatura + Conclusão da obra</SelectItem>
                  <SelectItem value="3">3 - Assinatura + Início + Conclusão da obra</SelectItem>
                  <SelectItem value="4">4 - Assinatura + Início + Conclusão + Ligação definitiva</SelectItem>
                  <SelectItem value="5">5 - Pagamento integral na conclusão da obra</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Formas de Pagamento por Etapa */}
          <div className="space-y-3 sm:space-y-4">
            <h5 className="text-sm sm:text-base font-semibold text-green-700">Formas de Pagamento por Etapa</h5>
            
            {/* Modalidade 1: Pagamento integral na assinatura */}
            {dadosContrato.integracao_modalidade_pagamento === 1 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_assinatura">Forma de Pagamento - Assinatura *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_assinatura} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_assinatura: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Parcelas para Assinatura quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_assinatura === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_assinatura">Número de Parcelas - Assinatura</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_assinatura.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_assinatura: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_assinatura">Valor de cada Parcela - Assinatura (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_assinatura"
                        value={campoEditando === 'integracao_valor_parcela_assinatura' 
                          ? valoresEditando.integracao_valor_parcela_assinatura || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_assinatura)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_assinatura: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_assinatura');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: dadosContrato.integracao_valor_parcela_assinatura.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_assinatura?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modalidade 2: Assinatura + Conclusão */}
            {dadosContrato.integracao_modalidade_pagamento === 2 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_assinatura">Forma de Pagamento - Assinatura *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_assinatura} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_assinatura: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_conclusao_obra">Forma de Pagamento - Conclusão da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_conclusao_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_conclusao_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Parcelas para Assinatura quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_assinatura === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_assinatura">Número de Parcelas - Assinatura</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_assinatura.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_assinatura: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_assinatura">Valor de cada Parcela - Assinatura (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_assinatura"
                        value={campoEditando === 'integracao_valor_parcela_assinatura' 
                          ? valoresEditando.integracao_valor_parcela_assinatura || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_assinatura)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_assinatura: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_assinatura');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: dadosContrato.integracao_valor_parcela_assinatura.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_assinatura?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Conclusão quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_conclusao_obra">Número de Parcelas - Conclusão</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_conclusao_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_conclusao_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_conclusao_obra">Valor de cada Parcela - Conclusão (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_conclusao_obra"
                        value={campoEditando === 'integracao_valor_parcela_conclusao_obra' 
                          ? valoresEditando.integracao_valor_parcela_conclusao_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_conclusao_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_conclusao_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_conclusao_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: dadosContrato.integracao_valor_parcela_conclusao_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_conclusao_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modalidade 3: Assinatura + Início + Conclusão */}
            {dadosContrato.integracao_modalidade_pagamento === 3 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_assinatura">Forma de Pagamento - Assinatura *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_assinatura} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_assinatura: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_inicio_obra">Forma de Pagamento - Início da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_inicio_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_inicio_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_conclusao_obra">Forma de Pagamento - Conclusão da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_conclusao_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_conclusao_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Parcelas para Assinatura quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_assinatura === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_assinatura">Número de Parcelas - Assinatura</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_assinatura.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_assinatura: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_assinatura">Valor de cada Parcela - Assinatura (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_assinatura"
                        value={campoEditando === 'integracao_valor_parcela_assinatura' 
                          ? valoresEditando.integracao_valor_parcela_assinatura || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_assinatura)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_assinatura: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_assinatura');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: dadosContrato.integracao_valor_parcela_assinatura.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_assinatura?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Início quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_inicio_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_inicio_obra">Número de Parcelas - Início da Obra</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_inicio_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_inicio_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_inicio_obra">Valor de cada Parcela - Início da Obra (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_inicio_obra"
                        value={campoEditando === 'integracao_valor_parcela_inicio_obra' 
                          ? valoresEditando.integracao_valor_parcela_inicio_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_inicio_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_inicio_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_inicio_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_inicio_obra: dadosContrato.integracao_valor_parcela_inicio_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_inicio_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_inicio_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Conclusão quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_conclusao_obra">Número de Parcelas - Conclusão</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_conclusao_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_conclusao_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_conclusao_obra">Valor de cada Parcela - Conclusão (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_conclusao_obra"
                        value={campoEditando === 'integracao_valor_parcela_conclusao_obra' 
                          ? valoresEditando.integracao_valor_parcela_conclusao_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_conclusao_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_conclusao_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_conclusao_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: dadosContrato.integracao_valor_parcela_conclusao_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_conclusao_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modalidade 4: Assinatura + Início + Conclusão + Ligação */}
            {dadosContrato.integracao_modalidade_pagamento === 4 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_assinatura">Forma de Pagamento - Assinatura *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_assinatura} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_assinatura: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_inicio_obra">Forma de Pagamento - Início da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_inicio_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_inicio_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_conclusao_obra">Forma de Pagamento - Conclusão da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_conclusao_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_conclusao_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_ligacao_definitiva">Forma de Pagamento - Ligação Definitiva *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_ligacao_definitiva} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_ligacao_definitiva: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Parcelas para Assinatura quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_assinatura === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_assinatura">Número de Parcelas - Assinatura</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_assinatura.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_assinatura: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_assinatura">Valor de cada Parcela - Assinatura (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_assinatura"
                        value={campoEditando === 'integracao_valor_parcela_assinatura' 
                          ? valoresEditando.integracao_valor_parcela_assinatura || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_assinatura)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_assinatura: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_assinatura');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: dadosContrato.integracao_valor_parcela_assinatura.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_assinatura?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_assinatura: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Início quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_inicio_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_inicio_obra">Número de Parcelas - Início da Obra</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_inicio_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_inicio_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_inicio_obra">Valor de cada Parcela - Início da Obra (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_inicio_obra"
                        value={campoEditando === 'integracao_valor_parcela_inicio_obra' 
                          ? valoresEditando.integracao_valor_parcela_inicio_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_inicio_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_inicio_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_inicio_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_inicio_obra: dadosContrato.integracao_valor_parcela_inicio_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_inicio_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_inicio_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Conclusão quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_conclusao_obra">Número de Parcelas - Conclusão</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_conclusao_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_conclusao_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_conclusao_obra">Valor de cada Parcela - Conclusão (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_conclusao_obra"
                        value={campoEditando === 'integracao_valor_parcela_conclusao_obra' 
                          ? valoresEditando.integracao_valor_parcela_conclusao_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_conclusao_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_conclusao_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_conclusao_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: dadosContrato.integracao_valor_parcela_conclusao_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_conclusao_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
                
                {/* Parcelas para Ligação Definitiva quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_ligacao_definitiva === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_ligacao_definitiva">Número de Parcelas - Ligação Definitiva</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_ligacao_definitiva.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_ligacao_definitiva: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_ligacao_definitiva">Valor de cada Parcela - Ligação Definitiva (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_ligacao_definitiva"
                        value={campoEditando === 'integracao_valor_parcela_ligacao_definitiva' 
                          ? valoresEditando.integracao_valor_parcela_ligacao_definitiva || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_ligacao_definitiva)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_ligacao_definitiva: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_ligacao_definitiva');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_ligacao_definitiva: dadosContrato.integracao_valor_parcela_ligacao_definitiva.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_ligacao_definitiva?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_ligacao_definitiva: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modalidade 5: Pagamento integral na conclusão */}
            {dadosContrato.integracao_modalidade_pagamento === 5 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="integracao_forma_pagamento_conclusao_obra">Forma de Pagamento - Conclusão da Obra *</Label>
                    <Select 
                      value={dadosContrato.integracao_forma_pagamento_conclusao_obra} 
                      onValueChange={(value) => {
                        setDadosContrato(prev => ({ ...prev, integracao_forma_pagamento_conclusao_obra: value }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a forma de pagamento" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="boleto">Pagamento à vista via Boleto</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                        <SelectItem value="cartao_debito">Cartão de Débito</SelectItem>
                        <SelectItem value="financiamento_bancario">Financiamento via instituição bancária</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Parcelas para Conclusão quando for cartão de crédito */}
                {dadosContrato.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <Label htmlFor="integracao_numero_parcelas_conclusao_obra">Número de Parcelas - Conclusão</Label>
                      <Select 
                        value={dadosContrato.integracao_numero_parcelas_conclusao_obra.toString()} 
                        onValueChange={(value) => {
                          const parcelas = parseInt(value);
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_numero_parcelas_conclusao_obra: parcelas
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o número de parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} parcela{num > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="integracao_valor_parcela_conclusao_obra">Valor de cada Parcela - Conclusão (R$)</Label>
                      <Input
                        id="integracao_valor_parcela_conclusao_obra"
                        value={campoEditando === 'integracao_valor_parcela_conclusao_obra' 
                          ? valoresEditando.integracao_valor_parcela_conclusao_obra || ''
                          : formatarValor(dadosContrato.integracao_valor_parcela_conclusao_obra)}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setValoresEditando(prev => ({ ...prev, integracao_valor_parcela_conclusao_obra: valor }));
                        }}
                        onFocus={() => {
                          setCampoEditando('integracao_valor_parcela_conclusao_obra');
                          setValoresEditando(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: dadosContrato.integracao_valor_parcela_conclusao_obra.toString() 
                          }));
                        }}
                        onBlur={() => {
                          setCampoEditando(null);
                          const valorLimpo = valoresEditando.integracao_valor_parcela_conclusao_obra?.replace(',', '.') || '0';
                          const valor = parseFloat(valorLimpo) || 0;
                          setDadosContrato(prev => ({ 
                            ...prev, 
                            integracao_valor_parcela_conclusao_obra: valor 
                          }));
                        }}
                        placeholder="0,00"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Valor Total da Integração */}
          <div>
            <Label htmlFor="integracao_valor_total">Valor Total da Integração (R$)</Label>
            <Input
              id="integracao_valor_total"
              value={campoEditando === 'integracao_valor_total' 
                ? valoresEditando.integracao_valor_total
                : formatarValor(dadosContrato.integracao_valor_total)}
              onChange={(e) => {
                // Permite entrada de vírgula e ponto
                const valor = e.target.value;
                setValoresEditando(prev => ({ ...prev, integracao_valor_total: valor }));
              }}
              onFocus={() => {
                setCampoEditando('integracao_valor_total');
                setValoresEditando(prev => ({ 
                  ...prev, 
                  integracao_valor_total: dadosContrato.integracao_valor_total.toString() 
                }));
              }}
              onBlur={() => {
                setCampoEditando(null);
                const valorLimpo = (valoresEditando.integracao_valor_total ?? '').replace(',', '.');
                const valor = parseFloat(valorLimpo) || 0;
                // Atualiza integração e valor total do contrato com o valor digitado (pode divergir da proposta)
                atualizarValoresIntegracao(dadosContrato.integracao_modalidade_pagamento, valor);
              }}
              placeholder="0,00"
            />
          </div>

          {/* Percentuais e Valores por Etapa - Apenas para modalidades 2, 3 e 4 */}
          {(dadosContrato.integracao_modalidade_pagamento === 2 || 
            dadosContrato.integracao_modalidade_pagamento === 3 || 
            dadosContrato.integracao_modalidade_pagamento === 4) && (
            <div className="space-y-3 sm:space-y-4">
              <h5 className="text-sm sm:text-base font-semibold text-green-700">Distribuição dos Pagamentos</h5>
              <p className="text-xs sm:text-sm text-muted-foreground">A soma dos percentuais deve totalizar 100%</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Assinatura - Sempre presente nas modalidades 2, 3 e 4 */}
                <div className="space-y-2">
                  <Label htmlFor="integracao_percentual_assinatura">Assinatura do Contrato (%)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="integracao_percentual_assinatura"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={dadosContrato.integracao_percentual_assinatura}
                      onChange={(e) => {
                        const percentual = parseFloat(e.target.value) || 0;
                        setDadosContrato(prev => {
                          const valorTotal = prev.integracao_valor_total;
                          const valorAssinatura = (valorTotal * percentual) / 100;
                          
                          return { 
                            ...prev, 
                            integracao_percentual_assinatura: percentual,
                            integracao_valor_assinatura: valorAssinatura
                          };
                        });
                      }}
                      className="w-20"
                    />
                    <Input
                      value={dadosContrato.integracao_valor_assinatura.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                {/* Início da Obra - Apenas modalidades 3 e 4 */}
                {(dadosContrato.integracao_modalidade_pagamento === 3 || dadosContrato.integracao_modalidade_pagamento === 4) && (
                  <div className="space-y-2">
                    <Label htmlFor="integracao_percentual_inicio_obra">Início da Obra (%)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="integracao_percentual_inicio_obra"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={dadosContrato.integracao_percentual_inicio_obra}
                        onChange={(e) => {
                          const percentual = parseFloat(e.target.value) || 0;
                          setDadosContrato(prev => {
                            const valorTotal = prev.integracao_valor_total;
                            const valorInicio = (valorTotal * percentual) / 100;
                            
                            return { 
                              ...prev, 
                              integracao_percentual_inicio_obra: percentual,
                              integracao_valor_inicio_obra: valorInicio
                            };
                          });
                        }}
                        className="w-20"
                      />
                      <Input
                        value={dadosContrato.integracao_valor_inicio_obra.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>
                )}

                {/* Conclusão da Obra - Sempre presente nas modalidades 2, 3 e 4 */}
                <div className="space-y-2">
                  <Label htmlFor="integracao_percentual_conclusao_obra">Conclusão da Obra (%)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="integracao_percentual_conclusao_obra"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={dadosContrato.integracao_percentual_conclusao_obra}
                      onChange={(e) => {
                        const percentual = parseFloat(e.target.value) || 0;
                        setDadosContrato(prev => {
                          const valorTotal = prev.integracao_valor_total;
                          const valorConclusao = (valorTotal * percentual) / 100;
                          
                          return { 
                            ...prev, 
                            integracao_percentual_conclusao_obra: percentual,
                            integracao_valor_conclusao_obra: valorConclusao
                          };
                        });
                      }}
                      className="w-20"
                    />
                    <Input
                      value={dadosContrato.integracao_valor_conclusao_obra.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      disabled
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                {/* Ligação Definitiva - Apenas modalidade 4 */}
                {dadosContrato.integracao_modalidade_pagamento === 4 && (
                  <div className="space-y-2">
                    <Label htmlFor="integracao_percentual_ligacao_definitiva">Após Ligação Definitiva (%)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="integracao_percentual_ligacao_definitiva"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={dadosContrato.integracao_percentual_ligacao_definitiva}
                        onChange={(e) => {
                          const percentual = parseFloat(e.target.value) || 0;
                          setDadosContrato(prev => {
                            const valorTotal = prev.integracao_valor_total;
                            const valorLigacao = (valorTotal * percentual) / 100;
                            
                            return { 
                              ...prev, 
                              integracao_percentual_ligacao_definitiva: percentual,
                              integracao_valor_ligacao_definitiva: valorLigacao
                            };
                          });
                        }}
                        className="w-20"
                      />
                      <Input
                        value={dadosContrato.integracao_valor_ligacao_definitiva.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Indicador de total */}
              <div className="flex justify-end">
                <div className="text-sm font-medium">
                  Total: {
                    (dadosContrato.integracao_percentual_assinatura + 
                     dadosContrato.integracao_percentual_inicio_obra + 
                     dadosContrato.integracao_percentual_conclusao_obra + 
                     dadosContrato.integracao_percentual_ligacao_definitiva).toFixed(2)
                  }%
                </div>
              </div>
              
              {/* Mensagem de aviso quando soma não for 100% */}
              {(() => {
                const totalPercentual = 
                  dadosContrato.integracao_percentual_assinatura + 
                  dadosContrato.integracao_percentual_inicio_obra + 
                  dadosContrato.integracao_percentual_conclusao_obra + 
                  dadosContrato.integracao_percentual_ligacao_definitiva;
                
                if (Math.abs(totalPercentual - 100) > 0.01) {
                  return (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        ⚠️ <strong>Atenção:</strong> A soma dos percentuais ({totalPercentual.toFixed(2)}%) não totaliza 100%. 
                        Por favor, ajuste os valores para que a soma seja exatamente 100%.
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}



          {/* Observações da Integração */}
          <div>
            <Label htmlFor="integracao_observacoes">Observações da Integração</Label>
            <Input
              id="integracao_observacoes"
              value={dadosContrato.integracao_observacoes}
              onChange={(e) => setDadosContrato(prev => ({ ...prev, integracao_observacoes: e.target.value }))}
              placeholder="Observações sobre o pagamento da integração (opcional)"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
        <Button 
          onClick={onSalvar} 
          disabled={
            loadingAction || (modo === 'formulario' ? !validarCamposObrigatorios() : !temMudancas())
          } 
          className="flex-1 w-full sm:w-auto"
          size="sm"
        >
          {loadingAction && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <span className="text-sm sm:text-base">{modo === 'formulario' ? 'Salvar Contrato' : 'Salvar Alterações'}</span>
        </Button>
        <Button variant="outline" onClick={onCancelar} size="sm" className="w-full sm:w-auto">
          <span className="text-sm sm:text-base">Cancelar</span>
        </Button>
      </div>
      
      {/* Mensagens de validação */}
      {modo === 'formulario' && !validarCamposObrigatorios() && (
        <Alert className="mt-4">
          <AlertDescription>
            ⚠️ Preencha todos os campos obrigatórios para salvar o contrato.
            {tipoCliente === 'PF' 
              ? ' Campos obrigatórios: Informações da Empresa (Razão Social, Nome Fantasia, CNPJ, Endereço completo, Representante Legal), Informações do Cliente (Nome, CPF, Estado Civil, Profissão, Endereço completo) e formas de pagamento.'
              : ' Campos obrigatórios: Informações da Empresa (Razão Social, Nome Fantasia, CNPJ, Endereço completo, Representante Legal), Informações do Cliente (Razão Social, CNPJ, Representante Legal, CPF do Representante, Endereço completo) e formas de pagamento.'
            }
          </AlertDescription>
        </Alert>
      )}
      
      {/* Mensagem de erro dos percentuais */}
      {obterMensagemValidacao() && (
        <Alert className="mt-4 border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">
            ❌ {obterMensagemValidacao()}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
